// FloorStackContainer — root container that wires:
//   - useFloorStack / useCommandView (data)
//   - reducer state machine (floorStackMachine)
//   - FloorStack (stack visual) ↔ FloorExpandedView (single floor)
//   - hover tooltip
//
// Renders one of two top-level branches based on state.kind:
//   * idle / hovering / expanding / collapsing → FloorStack with optional tooltip overlay
//   * expanded → FloorExpandedView

import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import EmptyState from "../components/EmptyState.js";
import Skeleton from "../components/Skeleton.js";
import { Building2, ServerCrash } from "lucide-react";
import { useFloorStack } from "./hooks/useFloorStack.js";
import { useCommandView } from "./hooks/useCommandView.js";
import { useFloorStackRealtime } from "./hooks/useFloorStackRealtime.js";
import { useVisualLayers } from "./hooks/useVisualLayers.js";
import FloorStack from "./FloorStack.js";
import FloorExpandedView from "./FloorExpandedView.js";
import AerialCard, { type AerialCardPhase } from "./AerialCard.js";
import AerialExpandedView from "./AerialExpandedView.js";
import { initial, reduce, reducedMotion, TIMINGS, type FloorStackState } from "./state/floorStackMachine.js";
import type { FloorStatusSnapshotDto, BuildingFloorDto } from "../lib/floorStackApi.js";
import { selectActiveBuilding, type ActiveFloor } from "./selectActiveBuilding.js";

/** Sentinel id used to drive the same expand / collapse state machine
 *  for the aerial tier. The machine treats this exactly like a floor
 *  id; the container special-cases the render branch to swap in
 *  AerialExpandedView instead of FloorExpandedView. */
const AERIAL_ID = "__AERIAL__";

interface Props {
  siteId: string;
  tenantId: string | null;
  /** Stack | mobile vertical list. Defaults to auto-detect via media query. */
  layoutMode?: "stack" | "list" | "auto";
}

export default function FloorStackContainer({ siteId, tenantId, layoutMode = "auto" }: Props): React.ReactElement {
  const [state, dispatch] = useReducer(reduce, initial);
  const [reduced, setReduced] = useState(false);
  const [forceMode, setForceMode] = useState<"stack" | "list" | null>(null);
  /** Operator-overridden active building id (set when user picks one
   *  from the aerial view's chip list). NULL = fall back to
   *  default_building_id then first-building, see selectActiveBuilding. */
  const [overrideBuildingId, setOverrideBuildingId] = useState<string | null>(null);
  const { state: stackQuery, reload: reloadStack } = useFloorStack(siteId, tenantId);
  const { state: aerialQuery } = useVisualLayers(siteId, tenantId);

  // Phase F realtime overlay — listens for snapshot / incident / patrol_event
  // frames from the realtime worker and triggers a reload when relevant.
  // Polling stays as a safety net (every 30s in useFloorStack).
  const { connection: realtimeConnection, onEvent: onRealtimeEvent } = useFloorStackRealtime(siteId, tenantId);
  useEffect(() => {
    return onRealtimeEvent((frame) => {
      if (
        frame.event.kind === "snapshot" ||
        frame.event.kind === "incident_open" ||
        frame.event.kind === "incident_ack" ||
        frame.event.kind === "incident_resolved"
      ) {
        void reloadStack();
      }
    });
  }, [onRealtimeEvent, reloadStack]);

  // The expanded floor's full dataset comes from /command-view.
  // BUT — the aerial tier reuses the same state machine via AERIAL_ID
  // sentinel; we don't want to fire /command-view for that case, so
  // pass null to the hook when the expanded id is aerial.
  const expandedFloorId = state.kind === "expanding" || state.kind === "expanded" || state.kind === "collapsing"
    ? state.floorId
    : null;
  const isAerialExpanded = expandedFloorId === AERIAL_ID;
  const { state: cmdQuery } = useCommandView(siteId, isAerialExpanded ? null : expandedFloorId, tenantId);

  // ── prefers-reduced-motion + viewport mode ────────────────────────────
  useEffect(() => {
    setReduced(reducedMotion());
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m1 = window.matchMedia("(prefers-reduced-motion: reduce)");
    const m2 = window.matchMedia("(max-width: 720px)");
    const upd = () => {
      setReduced(m1.matches);
      if (layoutMode === "auto") setForceMode(m2.matches ? "list" : "stack");
    };
    upd();
    m1.addEventListener("change", upd);
    m2.addEventListener("change", upd);
    return () => {
      m1.removeEventListener("change", upd);
      m2.removeEventListener("change", upd);
    };
  }, [layoutMode]);

  // Animation timer: expanding → expanded; collapsing → idle.
  useEffect(() => {
    if (state.kind === "expanding") {
      const t = setTimeout(() => dispatch({ type: "EXPAND_FINISHED" }), reduced ? 0 : TIMINGS.expandMs);
      return () => clearTimeout(t);
    }
    if (state.kind === "collapsing") {
      const t = setTimeout(() => dispatch({ type: "COLLAPSE_FINISHED" }), reduced ? 0 : TIMINGS.collapseMs);
      return () => clearTimeout(t);
    }
    return;
  }, [state.kind, reduced]);

  const onHover = useCallback((id: string) => dispatch({ type: "HOVER", floorId: id }), []);
  const onUnhover = useCallback(() => dispatch({ type: "UNHOVER" }), []);
  const onClick = useCallback((id: string) => dispatch({ type: "CLICK", floorId: id }), []);
  const onBack = useCallback(() => dispatch({ type: "BACK" }), []);

  /** From AerialExpandedView's chip list — swap active building and
   *  collapse back to the floor-stack tier. Passing null resets to
   *  default-building. */
  const onSelectBuildingFromAerial = useCallback((buildingId: string | null) => {
    setOverrideBuildingId(buildingId);
    dispatch({ type: "BACK" });
  }, []);

  // ── Derived data (hooks live above all early returns so React always
  //    sees the same hook order — this used to live after the
  //    loading/error gates which produced React error #310.) ────────────
  const data = stackQuery.status === "ready" ? stackQuery.data : null;
  const active = useMemo(() => selectActiveBuilding(data, overrideBuildingId), [data, overrideBuildingId]);
  const hoveringFloor: ActiveFloor | null = useMemo(() => {
    if (!active || state.kind !== "hovering") return null;
    return active.floors.find((f) => f.id === state.floorId) ?? null;
  }, [state, active]);

  // ── Loading / error gating ────────────────────────────────────────────
  if (stackQuery.status === "loading") return <Skeleton className="h-[480px] w-full" />;
  if (stackQuery.status === "error")
    return <EmptyState icon={ServerCrash} title="無法載入樓層預覽" description={stackQuery.message} action={
      <button onClick={reloadStack} className="btn">重試</button>
    } />;
  if (!data || !active)
    return <EmptyState icon={Building2} title="這個案場尚未建立任何建築 / 樓層" description="請到「樓層圖資編輯」建立第一棟與第一樓。" />;

  const { building, floors } = active;
  const mode = forceMode ?? (layoutMode === "auto" ? "stack" : layoutMode);

  // ── Rendering branches ────────────────────────────────────────────────
  // Aerial expanded view — sentinel-id branch. Reuses the same
  // expanding/expanded state machine; we just swap render targets.
  if (state.kind === "expanded" && isAerialExpanded) {
    const aerial = aerialQuery.status === "ready" ? aerialQuery.data : null;
    if (!aerial || !aerial.latest_capture) {
      // Should not happen — AerialCard only renders when has_aerial; if we
      // somehow lost the capture between click + render, fall back gracefully.
      return <EmptyState icon={Building2} title="鳥瞰圖資不可用" description="衛星擷取資料載入失敗，請回到樓層檢視重試。" action={
        <button onClick={onBack} className="btn">回樓層檢視</button>
      } />;
    }
    return (
      <AerialExpandedView
        siteId={siteId}
        siteName={data.site.name}
        tenantId={tenantId}
        capture={aerial.latest_capture}
        buildings={data.buildings}
        onSelectBuilding={onSelectBuildingFromAerial}
        onBack={onBack}
      />
    );
  }

  if ((state.kind === "expanded") && cmdQuery.status === "ready") {
    return <FloorExpandedView data={cmdQuery.data} tenantId={tenantId} onBack={onBack} reduced={reduced} />;
  }
  if (state.kind === "expanded" && cmdQuery.status === "loading") {
    return <Skeleton className="h-[480px] w-full" />;
  }
  if (state.kind === "expanded" && cmdQuery.status === "error") {
    return <EmptyState icon={ServerCrash} title="無法載入樓層中控視圖" description={cmdQuery.message} action={
      <button onClick={onBack} className="btn">回樓層檢視</button>
    } />;
  }

  // Aerial-tier wiring — only render when site has a satellite layer with
  // bbox we can use (latest_capture). 3D-only sites won't have a
  // latest_capture and degrade by hiding the aerial card; PR-F (3D viewer)
  // adds that branch.
  const aerial = aerialQuery.status === "ready" ? aerialQuery.data : null;
  const showAerial = !!aerial?.has_aerial && !!aerial.latest_capture && mode === "stack";
  const aerialPhase: AerialCardPhase = (() => {
    if (state.kind === "expanding" && state.floorId === AERIAL_ID) return "selected";
    if (state.kind === "collapsing" && state.floorId === AERIAL_ID) return "collapsing";
    if (state.kind === "hovering" && state.floorId === AERIAL_ID) return "hover";
    if (state.kind === "expanding" || state.kind === "expanded" || state.kind === "collapsing") return "dim";
    return "idle";
  })();

  return (
    <div className="floor-stack-container relative" data-state={state.kind}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-text-primary truncate">
          {building.name}
          {data.buildings.length > 1 && (
            <span className="ml-2 text-xs text-text-muted">（共 {data.buildings.length} 棟）</span>
          )}
        </h2>
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <RealtimeBadge state={realtimeConnection} />
          <span>{floors.length} 樓層 · 點擊進入單層中控</span>
        </div>
      </div>

      {showAerial && (
        <div className="aerial-row mx-auto mb-2 flex justify-center" style={{ width: "300px" }} data-testid="aerial-card-row">
          <AerialCard
            quality={aerial.quality}
            phase={aerialPhase}
            reduced={reduced}
            onHover={() => onHover(AERIAL_ID)}
            onUnhover={() => onUnhover()}
            onClick={() => onClick(AERIAL_ID)}
          />
        </div>
      )}

      <FloorStack
        floors={floors}
        state={state}
        reduced={reduced}
        onHover={onHover}
        onUnhover={onUnhover}
        onClick={onClick}
        mode={mode}
      />

      {/* Hover tooltip (only in stack mode) */}
      {mode === "stack" && hoveringFloor && (
        <FloorTooltip floor={hoveringFloor} />
      )}
    </div>
  );
}

interface TooltipProps {
  floor: BuildingFloorDto & { latest_snapshot: FloorStatusSnapshotDto | null };
}

function FloorTooltip({ floor }: TooltipProps): React.ReactElement {
  const snap = floor.latest_snapshot;
  return (
    <aside
      role="tooltip"
      data-testid="floor-tooltip"
      className="pointer-events-none absolute right-4 top-4 w-60 rounded-lg border border-border bg-bg-surface p-3 shadow-xl"
    >
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono text-base text-text-primary">{floor.floor_name}</span>
        {floor.is_critical === 1 && (
          <span className="text-xs text-amber-400">重點樓層</span>
        )}
      </div>
      {snap ? (
        <dl className="grid grid-cols-2 gap-y-1 text-xs">
          <dt className="text-text-secondary">完成率</dt>
          <dd className="font-mono tabular-nums">{snap.location_total > 0 ? `${Math.round(snap.completion_rate * 100)}%` : "—"}</dd>
          <dt className="text-text-secondary">未結事件</dt>
          <dd className="font-mono tabular-nums text-amber-400">{snap.incident_warning_count + snap.incident_critical_count}</dd>
          <dt className="text-text-secondary">在勤</dt>
          <dd className="font-mono tabular-nums">{snap.online_officers}</dd>
          <dt className="text-text-secondary">最後巡邏</dt>
          <dd className="text-text-secondary text-[10px]">{snap.last_patrol_at?.slice(0, 16).replace("T", " ") ?? "—"}</dd>
        </dl>
      ) : (
        <p className="text-xs text-text-muted">尚未有快照。</p>
      )}
    </aside>
  );
}


interface BadgeProps { state: import("./hooks/useFloorStackRealtime.js").ConnectionState }

function RealtimeBadge({ state }: BadgeProps): React.ReactElement {
  const map = {
    live: { label: "即時連線", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    fallback: { label: "備援連線", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
    connecting: { label: "連線中", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
    offline: { label: "離線（30s 輪詢）", cls: "bg-zinc-500/10 text-text-muted border-zinc-500/30" },
  } as const;
  const v = map[state];
  return (
    <span data-testid="realtime-badge" data-state={state}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] tabular-nums ${v.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" aria-hidden />
      {v.label}
    </span>
  );
}
